/**
 * FERP Portal - My Inbox Page (Layer 1 - Employee View)
 *
 * Unified inbox for regular employees showing:
 * - Notifications addressed to them (via recipientActorIds)
 * - Tasks assigned to them (via assignedActorId)
 *
 * FERP Principles:
 * - Dumb Frontend: All data from backend queries
 * - Data-driven: Uses ListNotificationsForRecipient, GetTasksByActor
 * - Consistent UI: Uses TabulatorPrimitiveManager (same pattern as Tasks, Workflows, etc.)
 */

import { useMemo } from 'react';
import {
  Container, Title, Text, Card, Group, Stack, Badge,
  ThemeIcon, Alert, Button, Tabs,
} from '@mantine/core';
import {
  IconInbox, IconAlertTriangle, IconRefresh,
  IconBell, IconCheckbox, IconCalendar,
} from '@tabler/icons-react';
import { useTenantId, useAuth } from '@/auth';
import { useMyActor } from '@/hooks/useMyActor';
import {
  useListNotificationsForRecipientQuery,
  useGetTasksByActorQuery,
  GqlTaskStatus,
  GqlNotificationStatus,
  type TaskFieldsFragment,
} from '@/generated/graphql';
import { TabulatorPrimitiveManager } from '@/components/primitives/TabulatorPrimitiveManager';
import type { PrimitiveItem } from '@/hooks/usePrimitiveData';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import type { EventInput } from '@fullcalendar/core';
import { getTaskStatusColor } from '@/pages/workflow-state/task-status-colors';

export function MyInboxPage() {
  const tenantId = useTenantId() ?? '';
  const skip = !tenantId;
  const { user } = useAuth();

  // DRR-061: Resolve auth user → backend actor ID (canonical hook)
  const { actorId: resolvedActorId } = useMyActor();
  const actorId = resolvedActorId ?? '';

  const {
    data: notifData, loading: notifLoading, error: notifError, refetch: refetchNotifs,
  } = useListNotificationsForRecipientQuery({
    variables: { recipientActorId: actorId, limit: 50 },
    skip: skip || !actorId,
  });

  const {
    data: taskData, loading: taskLoading, error: taskError, refetch: refetchTasks,
  } = useGetTasksByActorQuery({
    variables: { actorId },
    skip: skip || !actorId,
  });

  // Normalize notifications: map notificationTitle → name so Tabulator name column works
  const notifications = useMemo(
    () => (notifData?.listNotificationsForRecipient?.items ?? []).map((n) => ({
      ...n,
      name: n.notificationTitle,
    })) as unknown as PrimitiveItem[],
    [notifData],
  );

  const activeTasks = useMemo(
    () => (taskData?.getTasksByActor ?? []).filter((t: TaskFieldsFragment) =>
      t.status !== GqlTaskStatus.COMPLETED &&
      t.status !== GqlTaskStatus.CANCELLED &&
      t.status !== GqlTaskStatus.COMPENSATED,
    ) as unknown as PrimitiveItem[],
    [taskData],
  );

  // Convert tasks to calendar events
  const calendarEvents = useMemo<EventInput[]>(() => {
    const tasks = taskData?.getTasksByActor ?? [];
    return tasks.map((task: TaskFieldsFragment) => {
      const colors = getTaskStatusColor(task.status);
      const start = task.scheduledStart ?? task.startedAt ?? task.createdAt;
      const estimatedMs = task.estimatedDurationMs
        ? parseInt(task.estimatedDurationMs, 10)
        : 3_600_000; // 1 hour default
      const end = task.scheduledEnd ?? task.completedAt ??
        new Date(new Date(start).getTime() + estimatedMs).toISOString();

      return {
        id: task.id,
        title: task.name,
        start,
        end,
        backgroundColor: colors.bg,
        borderColor: colors.border,
        textColor: '#fff',
        extendedProps: { status: task.status, priority: task.priority },
      };
    });
  }, [taskData]);

  const unreadCount = useMemo(
    () => (notifData?.listNotificationsForRecipient?.items ?? []).filter((n) =>
      n.status === GqlNotificationStatus.PENDING || n.status === GqlNotificationStatus.SENT,
    ).length,
    [notifData],
  );

  const loading = notifLoading || taskLoading;

  const handleRefresh = () => {
    refetchNotifs();
    refetchTasks();
  };

  if (!tenantId || !user?.id) {
    return (
      <Container size="lg" py="xl">
        <Alert icon={<IconAlertTriangle size={16} />} color="yellow" title="Not Signed In">
          Please sign in to view your inbox.
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        <Group justify="space-between">
          <div>
            <Title order={2}>My Inbox</Title>
            <Text c="dimmed" size="sm">Your notifications, tasks, and action items</Text>
          </div>
          <Button leftSection={<IconRefresh size={16} />} variant="light" onClick={handleRefresh} loading={loading}>
            Refresh
          </Button>
        </Group>

        {/* Summary Cards */}
        <Group gap="md">
          <Card withBorder p="md" style={{ flex: 1 }}>
            <Group gap="xs">
              <ThemeIcon variant="light" size="sm" color="orange"><IconBell size={14} /></ThemeIcon>
              <div>
                <Text size="xs" c="dimmed">Unread</Text>
                <Text size="xl" fw={700}>{unreadCount}</Text>
              </div>
            </Group>
          </Card>
          <Card withBorder p="md" style={{ flex: 1 }}>
            <Group gap="xs">
              <ThemeIcon variant="light" size="sm" color="cyan"><IconCheckbox size={14} /></ThemeIcon>
              <div>
                <Text size="xs" c="dimmed">Active Tasks</Text>
                <Text size="xl" fw={700}>{activeTasks.length}</Text>
              </div>
            </Group>
          </Card>
          <Card withBorder p="md" style={{ flex: 1 }}>
            <Group gap="xs">
              <ThemeIcon variant="light" size="sm"><IconInbox size={14} /></ThemeIcon>
              <div>
                <Text size="xs" c="dimmed">Total Notifications</Text>
                <Text size="xl" fw={700}>{notifications.length}</Text>
              </div>
            </Group>
          </Card>
        </Group>

        {/* Tabbed Content */}
        <Tabs defaultValue="notifications">
          <Tabs.List>
            <Tabs.Tab value="notifications" leftSection={<IconBell size={14} />}>
              Notifications {unreadCount > 0 && (
                <Badge size="xs" color="red" variant="filled" ml={4}>{unreadCount}</Badge>
              )}
            </Tabs.Tab>
            <Tabs.Tab value="tasks" leftSection={<IconCheckbox size={14} />}>
              My Tasks {activeTasks.length > 0 && (
                <Badge size="xs" color="cyan" variant="filled" ml={4}>{activeTasks.length}</Badge>
              )}
            </Tabs.Tab>
            <Tabs.Tab value="calendar" leftSection={<IconCalendar size={14} />}>
              Calendar
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="notifications" pt="md">
            {notifError && (
              <Alert icon={<IconAlertTriangle size={16} />} color="orange" title="Notifications Unavailable" mb="md">
                Could not load notifications. Tasks are still available below.
              </Alert>
            )}
            <TabulatorPrimitiveManager
              primitiveType="notification"
              compact
              overrideData={{
                items: notifications,
                loading: notifLoading,
                error: notifError,
                refetch: refetchNotifs,
              }}
            />
          </Tabs.Panel>

          <Tabs.Panel value="tasks" pt="md">
            {taskError && (
              <Alert icon={<IconAlertTriangle size={16} />} color="orange" title="Tasks Unavailable" mb="md">
                Could not load tasks.
              </Alert>
            )}
            <TabulatorPrimitiveManager
              primitiveType="task"
              compact
              overrideData={{
                items: activeTasks,
                loading: taskLoading,
                error: taskError,
                refetch: refetchTasks,
              }}
            />
          </Tabs.Panel>

          <Tabs.Panel value="calendar" pt="md">
            <Card withBorder p="md">
              <FullCalendar
                plugins={[dayGridPlugin, timeGridPlugin, listPlugin]}
                initialView="dayGridMonth"
                headerToolbar={{
                  left: 'prev,next today',
                  center: 'title',
                  right: 'dayGridMonth,timeGridWeek,listWeek',
                }}
                events={calendarEvents}
                height="auto"
                nowIndicator
                dayMaxEvents={4}
                slotMinTime="06:00:00"
                slotMaxTime="22:00:00"
              />
            </Card>
          </Tabs.Panel>
        </Tabs>
      </Stack>
    </Container>
  );
}

export default MyInboxPage;
